import get from 'lodash/get';
import moment from 'moment';
import type { FilterOrder } from '../auxiliaryFields';

// field transformers are used to prepare data for serialization and deserialization to query string

export abstract class FieldTransformer {
  constructor(public name: string) {}
  abstract toRequestQuery: (values: any) => any;
  abstract fromPageQuery: (values: any) => any;
  abstract toPageQuery: (values: any) => any;
  transformResult = (result: any) => ({
    [this.name]: typeof result == 'string' ? result.trim() : result,
  });
}

export class RangeFieldTransformer extends FieldTransformer {
  fromPageQuery = (values: any) => {
    const inputKeys = this.getInputKeys();
    return this.transformResult({
      max: get(values, inputKeys.max),
      min: get(values, inputKeys.min),
    });
  };
  toRequestQuery = (values: any) => {
    const inputKeys = this.getInputKeys();
    const outputKeys = this.getOutputKeys();
    return {
      [inputKeys.max]: get(values, outputKeys.max),
      [inputKeys.min]: get(values, outputKeys.min),
    };
  };
  toPageQuery = this.toRequestQuery;

  getInputKeys = () => ({ max: `${this.name}<`, min: `${this.name}>` });
  getOutputKeys = () => ({ max: `${this.name}.max`, min: `${this.name}.min` });
}

export class GenericFieldTransformer extends FieldTransformer {
  fromPageQuery = (values: any) => this.transformResult(get(values, this.name));
  toRequestQuery = (values: any) => this.transformResult(get(values, this.name));
  toPageQuery = this.toRequestQuery;
}

export class CheckboxFieldTransformer extends FieldTransformer {
  fromPageQuery = (values: any) => {
    const value = get(values, this.name);
    let result = undefined;
    if (value === '1') {
      result = true;
    } else if (value === '0') {
      result = false;
    }
    return this.transformResult(result);
  };
  toRequestQuery = (values: any) => this.transformResult(get(values, this.name) ? '1' : '0');
  toPageQuery = this.toRequestQuery;
}

export class FieldWithEmptyValueTransformer extends FieldTransformer {
  constructor(name: string, public emptyValue: string) {
    super(name);
  }

  fromPageQuery = (values: any) => {
    const result = get(values, this.name);
    return this.transformResult(result || this.emptyValue);
  };
  toRequestQuery = (values: any) => {
    const result = get(values, this.name);
    return this.transformResult(result === this.emptyValue ? '' : result);
  };
  toPageQuery = this.toRequestQuery;
}

export class OrderFieldTransformer extends FieldTransformer {
  fromPageQuery = (values: any) => {
    const value = get(values, this.name) ?? '';

    const regexp = /(?<descending>-?)(?<key>.*)/;
    const match = regexp.exec(value);

    const key = match?.groups?.key ?? '';
    const isAscending = key ? !match?.groups?.descending : false;

    return this.transformResult({ isAscending, key } as FilterOrder);
  };
  toRequestQuery = (values: any) => {
    const value = get(values, this.name) as FilterOrder;

    if (!value?.key) return this.transformResult('');

    const result = `${value.isAscending ? '' : '-'}${value.key}`;
    return this.transformResult(result);
  };
  toPageQuery = this.toRequestQuery;
}

export class EmptyPageQueryTransformer extends FieldTransformer {
  fromPageQuery = (values: any) => this.transformResult(get(values, this.name));
  toRequestQuery = (values: any) => this.transformResult(get(values, this.name));
  toPageQuery = (values: any) => this.transformResult('');
}

export class DateTimeRangeFieldTransformer extends FieldTransformer {
  fromPageQuery = (values: any) => {
    const startKey = `${this.name}>`;
    const endKey = `${this.name}<`;
    return this.transformResult({
      start: get(values, startKey),
      end: get(values, endKey),
    });
  };

  // Make the selected end day inclusive: the picker emits the end as that day's midnight, and the
  // API uses a strict `<`, which would otherwise exclude the whole end day. We can't switch to `<=`
  // (the query-string library encodes it to a broken `<==`), so shift the end bound to the start of
  // the next day instead. Done only for the request — the page query keeps the user's picked day so
  // it round-trips back through the picker unchanged.
  private nextDay = (end: string) => {
    // parseZone keeps the original timezone offset (e.g. -04:00) so the serialized form matches start.
    const parsed = moment.parseZone(end);
    return parsed.isValid() ? parsed.add(1, 'day').format() : end;
  };

  // Emit both keys unconditionally (mirroring RangeFieldTransformer). Empty bounds serialize to ''
  // and are dropped downstream by omitEmptyValues. This is required so the keys are present in
  // keysToRemove (built from the default values) in useFilterForm — otherwise an applied date
  // filter can never be cleared from the URL/request.
  private serialize = (values: any, transformEnd: (end: string) => string) => {
    const value = get(values, this.name) ?? {};
    return {
      [`${this.name}>`]: value.start ?? '',
      [`${this.name}<`]: value.end ? transformEnd(value.end) : '',
    };
  };

  toRequestQuery = (values: any) => this.serialize(values, this.nextDay);

  toPageQuery = (values: any) => this.serialize(values, end => end);
}
